import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Asks before leaving a page with unsaved changes.
//
// The app uses <BrowserRouter>, not a data router, so react-router's useBlocker
// is not available. This covers the two ways out instead:
//   - closing or reloading the tab, or leaving the site: the browser's own
//     "leave site?" prompt (beforeunload; browsers do not allow custom text);
//   - clicking an in-app link (<Link>, the sidebar, breadcrumbs): the click is
//     stopped before React Router sees it and handed to `onBlockedNavigation`
//     with a `proceed` callback that performs the navigation later.
// Navigation started from code (navigate() in a click handler) is not caught.
//
// A guard that sits inside a page with its own tabs can also expose a
// LeaveGuard to its parent, so switching tabs goes through the same prompt.

// Returns true when it took over (there are unsaved changes and the caller must
// wait for `proceed`), false when the caller may leave right away.
export type LeaveGuard = (proceed: () => void) => boolean;

type Options = {
  isDirty: boolean;
  onBlockedNavigation: (proceed: () => void) => void;
};

const isPlainLeftClick = (event: MouseEvent) => event.button === 0
  && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

export const useUnsavedChangesGuard = ({ isDirty, onBlockedNavigation }: Options) => {
  const navigate = useNavigate();
  const onBlockedRef = useRef(onBlockedNavigation);
  onBlockedRef.current = onBlockedNavigation;

  useEffect(() => {
    if (!isDirty) return undefined;

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Older browsers need returnValue set to show the prompt.
      event.returnValue = '';
    };

    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || !isPlainLeftClick(event)) return;
      const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor || (anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download')) return;
      const url = new URL(anchor.href, window.location.href);
      // Another site: beforeunload asks.
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      event.preventDefault();
      event.stopPropagation();
      onBlockedRef.current(() => navigate(`${url.pathname}${url.search}${url.hash}`));
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    // Capture phase on the document runs before React's own listeners on the
    // root, so stopping the event here keeps <Link> from navigating.
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [isDirty, navigate]);
};
